import {isNil} from 'ramda';

import {thumbnailUrl} from '@dms/shared';

// The result is a photo, so JPEG keeps the data URL several times smaller than PNG.
const PREVIEW_JPEG_QUALITY = 0.9;

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = document.createElement('img');
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The watermark preview image did not load'));
    image.src = url;
  });

/**
 * Draws a rendered watermark over the reference photo of the composition editor and returns the
 * result as a data URL. The placement mirrors what the BE does with a saved composition (full size,
 * centered, fit to the photo), so the unsaved preview looks like the saved settings preview.
 */
export const composeOnReferencePhoto = async (watermarkUrl: string) => {
  const [photo, watermark] = await Promise.all([
    loadImage(`/${thumbnailUrl}`),
    loadImage(watermarkUrl),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = photo.naturalWidth;
  canvas.height = photo.naturalHeight;
  const context = canvas.getContext('2d');
  if (isNil(context)) {
    throw new Error('The browser does not support a 2D canvas');
  }

  const scale = Math.min(
    canvas.width / watermark.naturalWidth,
    canvas.height / watermark.naturalHeight
  );
  const width = watermark.naturalWidth * scale;
  const height = watermark.naturalHeight * scale;

  context.drawImage(photo, 0, 0);
  context.drawImage(
    watermark,
    (canvas.width - width) / 2,
    (canvas.height - height) / 2,
    width,
    height
  );

  return canvas.toDataURL('image/jpeg', PREVIEW_JPEG_QUALITY);
};
